import { Fragment, useRef, useMemo, useCallback } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import clsx from 'clsx'
import { AgGridReact } from 'ag-grid-react'

import { AgGridActionCellRenderer } from '../components'

const DialogReorderTipsToolsItems = ({
  open = false,
  inProgress = false,
  title = '',
  text = '',
  yesText = 'Yes',
  noText = 'No',
  inProgressButtonText = 'Please wait...',
  rowData = [],
  onConfirm = () => {},
  onCancel = () => {},
}) => {
  const gridRef = useRef()
  const defaultColDef = useMemo(() => {
    return {
      editable: false,
      resizable: true,
    }
  }, [])

  const refreshCells = useCallback(() => {
    if (!open) {
      return
    }

    const params = {
      force: true,
      suppressFlash: true,
    }
    gridRef.current?.api?.refreshCells(params)
  }, [open])

  const getRowId = useCallback((params) => params.data.postId, [])

  const onRowDragEnd = useCallback(() => {
    refreshCells()
  }, [refreshCells])

  const handleConfirm = () => {
    let newRowData = []
    gridRef.current.api.forEachNode((node) => {
      newRowData.push(node.data)
    })

    onConfirm(Array.from(newRowData))
  }

  const columnDefs = useMemo(
    () => [
      {
        field: 'order',
        width: 80,
        cellRenderer: (params) => {
          return (
            <AgGridActionCellRenderer
              {...params}
              editEnabled={false}
              addEnabled={false}
              removeEnabled={false}
            />
          )
        },
      },
      {
        field: 'title',
        flex: 1,
        cellRenderer: (params) => (
          <div dangerouslySetInnerHTML={{ __html: params.value }} />
        ),
      },
    ],
    []
  )

  return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-40" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel
                  className={clsx(
                    'group w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-sm transition-all',
                    inProgress && 'inProgress'
                  )}
                >
                  <Dialog.Title
                    as="h3"
                    className="mb-5 text-xl font-bold leading-none text-black-olive"
                  >
                    {title}
                  </Dialog.Title>
                  <div className="mt-2">
                    <div
                      className="text-sm"
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  </div>

                  {/** List */}
                  <div className="ag-theme-alpine h-[50vh] w-full">
                    <AgGridReact
                      ref={gridRef}
                      rowData={rowData}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      editType={'fullRow'}
                      suppressClickEdit={true}
                      stopEditingWhenCellsLoseFocus={true}
                      animateRows={true}
                      rowDragManaged={true}
                      getRowId={getRowId}
                      onRowDragEnd={onRowDragEnd}
                    ></AgGridReact>
                  </div>

                  <div className="mt-4 text-right">
                    <button
                      type="button"
                      className="rounded-full border-2 border-skin-button-accent bg-skin-button-accent px-6 py-2 text-xs leading-none text-white transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={handleConfirm}
                    >
                      {inProgress ? inProgressButtonText : yesText}
                    </button>
                    <button
                      type="button"
                      className="ml-2 rounded-full border-2 border-skin-button-accent px-6 py-2 text-xs leading-none transition-colors group-[.inProgress]:cursor-not-allowed group-[.inProgress]:opacity-50 group-[:not(.inProgress)]:hover:bg-skin-button-accent group-[:not(.inProgress)]:hover:text-white group-[:not(.inProgress)]:active:translate-y-[0.75px] group-[:not(.inProgress)]:active:scale-[0.99]"
                      disabled={inProgress}
                      onClick={() => onCancel()}
                    >
                      {noText}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default DialogReorderTipsToolsItems
